import { useRequest } from "hooks/useRequest";
import { IForecastReal } from "domain/Predictive";
import { IHomeStatistics } from "domain/Utils/models/IHomeStatistics";
import { ITimeScope } from "domain/Domain/Models/ITimeScope";
import { DateTime } from "luxon";
import { IDemandDataVM } from "../models/IDemandDataVM";
import { IForecastDataVM } from "../models/IForecastDataVM";
import { IAvailableModel } from "../models/IAvailableModel";

export const useModelAnalysisRequester = () => {
	const request = useRequest("DemandForecast");

	return {
		getDemand: async (productClientId: string, storeClientId?: string | null) => {
			const query = storeClientId ? `?storeClientId=${encodeURIComponent(storeClientId)}` : '';
			const c = await request.GET(`product/${productClientId}/demand${query}`);
			return c.data as IDemandDataVM;
		},
		
		getForecasts: async (productClientId: string, modelNames: string[], storeClientId?: string | null) => {
			const storeQuery = storeClientId ? `&storeClientId=${encodeURIComponent(storeClientId)}` : '';
			const promises = modelNames.map(m => request.GET(`product/${productClientId}/forecast?modelName=${encodeURIComponent(m)}${storeQuery}`));
			const responses = await Promise.all(promises);
			return responses.map(r => r.data as IForecastDataVM);
		},

		mapToStatistics: (demandData: IDemandDataVM, forecasts: IForecastDataVM[], dateRange?: ITimeScope) => {
			const historicalDemandDict = (demandData.historicalDemand || (demandData as any).HistoricalDemand || {}) as { [key: string]: number };
			
			const m1 = forecasts.length > 0 ? forecasts[0] : null;
			const m2 = forecasts.length > 1 ? forecasts[1] : null;

			const modelForecastDict = m1 ? (m1.modelForecast || (m1 as any).ModelForecast || {}) as { [key: string]: number } : {};
			const benchmarkForecastDict = m2 ? (m2.modelForecast || (m2 as any).ModelForecast || {}) as { [key: string]: number } : {};
			
			let allDates = Array.from(new Set([
				...Object.keys(historicalDemandDict),
				...Object.keys(modelForecastDict),
				...Object.keys(benchmarkForecastDict)
			]));
			allDates.sort();

			if (dateRange && dateRange.startDate && dateRange.endDate) {
				allDates = allDates.filter(dateStr => {
					const dt = DateTime.fromISO(dateStr);
					return dt >= dateRange.startDate! && dt <= dateRange.endDate!;
				});
			}

			const evaluationDemand: { [key: string]: IForecastReal } = {};
			const benchmarkDemand: { [key: string]: IForecastReal } = {};

			allDates.forEach((date) => {
				const realValue = historicalDemandDict[date] ?? null;
				const forecastValue = modelForecastDict[date] ?? null;
				const benchmarkValue = benchmarkForecastDict[date] ?? null;

				evaluationDemand[date] = {
					real: realValue,
					forecast: forecastValue,
					min: null,
					max: null,
					error: null,
				} as unknown as IForecastReal;

				benchmarkDemand[date] = {
					real: null,
					forecast: benchmarkValue,
					min: null,
					max: null,
					error: null,
				} as unknown as IForecastReal;
			});

			const safeEvaluation = {
				rmse: 0, r2: 0, mpe: 0, mape: 0, cv: demandData.cv ?? 0,
			};

			const aggregateMonthly = (dailyDemand: { [key: string]: IForecastReal }) => {
				const monthlyDemand: { [key: string]: IForecastReal } = {};
				
				Object.keys(dailyDemand).forEach(dateStr => {
					const dt = DateTime.fromISO(dateStr);
					if (!dt.isValid) return;
					
					const monthKey = dt.startOf("month").toISODate()!;
					
					if (!monthlyDemand[monthKey]) {
						monthlyDemand[monthKey] = {
							real: null,
							forecast: null,
							min: null,
							max: null,
							error: null
						} as unknown as IForecastReal;
					}
					
					const current = monthlyDemand[monthKey];
					const daily = dailyDemand[dateStr];
					
					if (daily.real !== null && daily.real !== undefined) {
						current.real = (current.real || 0) + daily.real;
					}
					if (daily.forecast !== null && daily.forecast !== undefined) {
						current.forecast = (current.forecast || 0) + daily.forecast;
					}
					if (daily.min !== null && daily.min !== undefined) {
						current.min = (current.min || 0) + daily.min;
					}
					if (daily.max !== null && daily.max !== undefined) {
						current.max = (current.max || 0) + daily.max;
					}
				});
				
				return monthlyDemand;
			};

			const buildSafeEvaluationVM = (demandDict: { [key: string]: IForecastReal }, monthlyDemandDict: { [key: string]: IForecastReal }, sum: number, avg: number, stdev: number, model: IForecastDataVM | null) => {
				const evaluation = model ? {
					rmse: model.rmse ?? 0,
					r2: model.r2 ?? 0,
					mpe: model.pe ?? 0,
					mape: model.ape ?? 0,
					cv: demandData.cv ?? 0,
					mae: 0,
					me: 0
				} : safeEvaluation;

				return {
					demand: demandDict,
					monthlyDemand: monthlyDemandDict,
					demandEvaluationScope: {
						start: allDates.length > 0 ? allDates[0] : new Date().toISOString(),
						end: allDates.length > 0 ? allDates[allDates.length - 1] : new Date().toISOString(),
					},
					avgDemand: { value: avg, validCount: 0 },
					monthlyAvgDemand: { value: avg, validCount: 0 },
					avgDemandCost: { value: 0, validCount: 0 },
					monthlyAvgDemandCost: { value: 0, validCount: 0 },
					demandStdDev: stdev,
					monthlyDemandStdDev: stdev,
					forecastSum: { value: sum, validCount: 0 },
					forecastSumCost: { value: 0, validCount: 0 },
					virtualStockCount: { value: 0, validCount: 0 },
					dailyEvaluation: evaluation,
					monthlyEvaluation: evaluation,
					dailyEvaluationMin: evaluation,
					dailyEvaluationMax: evaluation,
					monthlyEvaluationMin: evaluation,
					monthlyEvaluationMax: evaluation,
				};
			};

			const forecastSum = Object.values(modelForecastDict).reduce((a, b) => a + (b || 0), 0);
			const benchmarkSum = Object.values(benchmarkForecastDict).reduce((a, b) => a + (b || 0), 0);

			const calculateSuperiority = (modelError: number, benchmarkError: number) => {
				if (!benchmarkError || benchmarkError === 0) return 0;
				return (benchmarkError - modelError) / benchmarkError;
			};

			const calculateLocalRMSE = (demandDict: { [key: string]: IForecastReal }, realDemandDict: { [key: string]: IForecastReal }) => {
				const dates = Object.keys(demandDict);
				if (dates.length === 0) return 0;
				
				let sumSquaredError = 0;
				let count = 0;
				
				dates.forEach(date => {
					const item = demandDict[date];
					const realItem = realDemandDict[date];
					if (realItem && realItem.real !== null && realItem.real !== undefined && item.forecast !== null && item.forecast !== undefined) {
						const error = realItem.real - item.forecast;
						sumSquaredError += error * error;
						count++;
					}
				});
				
				if (count === 0) return 0;
				return Math.sqrt(sumSquaredError / count);
			};

			const monthlyEvaluationDemand = aggregateMonthly(evaluationDemand);
			const monthlyBenchmarkDemand = aggregateMonthly(benchmarkDemand);

			const modelDailyRMSE = calculateLocalRMSE(evaluationDemand, evaluationDemand);
			const benchmarkDailyRMSE = calculateLocalRMSE(benchmarkDemand, evaluationDemand);
			const dailySuperiority = calculateSuperiority(modelDailyRMSE, benchmarkDailyRMSE);

			const modelMonthlyRMSE = calculateLocalRMSE(monthlyEvaluationDemand, monthlyEvaluationDemand);
			const benchmarkMonthlyRMSE = calculateLocalRMSE(monthlyBenchmarkDemand, monthlyEvaluationDemand);
			const monthlySuperiority = calculateSuperiority(modelMonthlyRMSE, benchmarkMonthlyRMSE);

			const mappedStatistics = {
				evaluation: buildSafeEvaluationVM(evaluationDemand, monthlyEvaluationDemand, forecastSum, demandData.mean ?? demandData.avg ?? m1?.avg ?? 0, m1?.stdev || demandData.stdev || 0, m1),
				benchmarkEvaluation: buildSafeEvaluationVM(benchmarkDemand, monthlyBenchmarkDemand, benchmarkSum, demandData.mean ?? demandData.avg ?? m2?.avg ?? 0, m2?.stdev || demandData.stdev || 0, m2),
				dailySuperiorityFactor: dailySuperiority,
				monthlySuperiorityFactor: monthlySuperiority,
			} as unknown as IHomeStatistics;

			return mappedStatistics;
		},
		getAvailableModels: () => {
			return request.GET(`available-forecast-models`).then((c) => {
				return (c.data || []) as IAvailableModel[];
			});
		}
	};
};
